import { useMemo, useState } from 'react'
import type { JSX } from 'react'

import { parse, unparse } from 'papaparse'

import {
  Button,
  FileTextIcon,
  Flex,
  Heading,
  Link,
  List,
  Notice,
  Paragraph,
  Strong,
  Text,
  UploadIcon,
} from '@buffer-mono/popcorn'

import { FileInput } from '~publish/components/FileInput'
import * as UploadDropzone from '~publish/components/UploadDropzone'
import type { PinterestBoard, Service } from '../../graphqlTypes'
import { useTagsQuery } from '~publish/hooks/useTags'

import { BulkPostDialogBody } from '../BulkPostDialog'
import type { ListItem } from '../PostItems'
import { CsvExampleTable } from './CsvExampleTable'
import { constructCsvTemplate } from './CsvTemplate'
import {
  getAllHeaders,
  getAllUserFriendlyHeaders,
  getDuplicateHeaders,
  getMissingHeaders,
  isRowEmpty,
} from './helpers'
import { validateAndConstructItem } from './validators'
import type { FormattedCsvLine } from './validators'

import styles from './CsvUploader.module.css'

const BULK_UPLOAD_GUIDE_URL =
  'https://support.buffer.com/articles/how-to-upload-posts-in-bulk-to-buffer-cTIhl4mv6H'

const DIRECT_IMAGE_LINK_GUIDE_URL =
  'https://support.buffer.com/articles/how-to-upload-posts-in-bulk-to-buffer-cTIhl4mv6H'

/**
 * First step of the Bulk Upload flow ("Prepare and upload your CSV file").
 * Walks the user through downloading the template, filling in the details (with
 * the feature's limitations surfaced up front), and uploading the file — all on
 * one screen, so they run into fewer errors during import. A successful upload
 * advances to the review step.
 */
export const CsvUploader = ({
  channelId,
  channelService,
  channelTimezone,
  isTwitterPremium = false,
  mastodonMaxCharacters,
  existingBoards,
  isFreePlan,
  postsRemaining,
  onUploadComplete,
}: {
  channelId: string
  channelService: Service
  channelTimezone: string
  isTwitterPremium: boolean
  mastodonMaxCharacters?: number
  existingBoards: PinterestBoard[]
  isFreePlan: boolean
  postsRemaining: number
  onUploadComplete: (items: ListItem[]) => string | void
}): JSX.Element => {
  const [error, setError] = useState<string | null>(null)
  const { tags } = useTagsQuery()
  const csvTemplate = useMemo(
    () =>
      constructCsvTemplate({
        channelService,
        existingTags: tags,
        existingBoards,
      }),
    [channelService, tags, existingBoards],
  )
  const allHeaders = useMemo(
    () => getAllHeaders(channelService),
    [channelService],
  )
  const allUserFriendlyHeaders = useMemo(
    () => getAllUserFriendlyHeaders(channelService),
    [channelService],
  )
  const parseCsv = (file: File): void => {
    setError(null)
    parse<FormattedCsvLine>(file, {
      header: true,
      skipEmptyLines: true,
      // Ensure proper handling of quoted fields
      quoteChar: '"',
      escapeChar: '"',
      delimitersToGuess: [',', ';'],
      // Forcibly remove quotes from headers as papaparse fails to handle them correctly if there are duplicate headers
      transformHeader: (header) =>
        header.replace(/"/g, '').replace(/ /g, '_').toLowerCase(),
      complete: (result) => {
        // Check for duplicates among required headers
        const headers = result.meta.fields ?? []
        const duplicateHeaders = getDuplicateHeaders({
          service: channelService,
          headers,
        })
        if (duplicateHeaders.length > 0) {
          setError(
            `Duplicate column${
              duplicateHeaders.length > 1 ? 's' : ''
            }: ${duplicateHeaders.join(', ')}`,
          )
          return
        }

        const missingHeaders = getMissingHeaders({
          service: channelService,
          headers: result.meta.fields,
        })
        if (missingHeaders.length > 0) {
          setError(
            `Invalid CSV file. Missing column${
              missingHeaders.length > 1 ? 's' : ''
            }: ${missingHeaders.join(', ')}`,
          )
          return
        }

        // Exclude completely empty rows
        const nonEmptyRows = result.data.filter((row) => !isRowEmpty(row))
        const items = nonEmptyRows.map((row) =>
          validateAndConstructItem({
            row,
            channelId,
            channelService,
            channelTimezone,
            isTwitterPremium,
            mastodonMaxCharacters,
            existingTags: tags,
            existingBoards,
          }),
        )
        const uploadError = onUploadComplete(items)
        if (uploadError) setError(uploadError)
      },
      error: () => {
        setError(
          "Can't upload your file. Please ensure the format follows the template.",
        )
      },
    })
  }
  const handleFileChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    const file = event.target.files?.[0]
    if (file) {
      parseCsv(file)
    }
  }

  const onUpload = (files: File[]): void => {
    const file = files[0]
    if (file) {
      parseCsv(file)
    }
  }

  return (
    <BulkPostDialogBody title="Prepare and upload your CSV file" scrollable>
      <Flex direction="column" align="stretch" className={styles.steps}>
        <Flex
          as="section"
          align="center"
          justify="between"
          gap="lg"
          className={styles.step}
        >
          <Flex direction="column" gap="2xs">
            <Heading size="small">1. Download the CSV template</Heading>
            <Paragraph color="subtle">
              We recommend starting with the template we’ve set up for you.
            </Paragraph>
          </Flex>
          <Button
            as="a"
            href={`data:text/csv;charset=utf-8,${encodeURIComponent(
              '\uFEFF' + // UTF-8 Byte Order Mark to identify the correct encoding
                unparse(csvTemplate, {
                  quotes: true,
                  columns: allUserFriendlyHeaders,
                  quoteChar: '"',
                  escapeChar: '"',
                  header: true,
                  skipEmptyLines: true,
                }),
            )}`}
            download="Buffer_Import_Template.csv"
            variant="primary"
            size="medium"
            className={styles.downloadButton}
          >
            <FileTextIcon /> Download Template
          </Button>
        </Flex>
        <Flex as="section" direction="column" gap="md" className={styles.step}>
          <Flex direction="column" gap="2xs">
            <Heading size="small">2. Fill in the details</Heading>
            <Paragraph color="subtle">
              Add one row per post, up to{' '}
              {isFreePlan
                ? `${postsRemaining} more post${
                    postsRemaining === 1 ? '' : 's'
                  } on your current plan`
                : '100 posts per upload'}
              . Here’s how your file should look:
            </Paragraph>
          </Flex>
          <CsvExampleTable headers={allHeaders} />
          <List>
            <List.Item>
              <Text>
                <Strong>Text</Strong> - Must not exceed the channel’s character
                limit. To use emoji, save the file with CSV UTF-8 or UTF-16
                encoding.
              </Text>
            </List.Item>
            <List.Item>
              <Text>
                <Strong>Image</Strong> - Use a public link ending in .jpg,
                .jpeg, or .png — one image per row, under 5MB. Right-click the
                link: it should open the image on its own. Video and private or
                cloud-drive links won’t load.{' '}
                <Link href={DIRECT_IMAGE_LINK_GUIDE_URL} external>
                  Learn how to get a direct image link
                </Link>
                .
              </Text>
            </List.Item>
            <List.Item>
              <Text>
                <Strong>Posting time</Strong> - Use the format YYYY-MM-DD HH:MM
                in 24-hour time (e.g. 2026-07-29 13:29). Leave it blank to add
                the post to your queue.
              </Text>
            </List.Item>
            <List.Item>
              <Text>
                <Strong>Tags</Strong> - Must already exist in Buffer
                (case-sensitive). Use a comma between tags.
              </Text>
            </List.Item>
          </List>
          <Paragraph color="subtle">
            Need more detail on formatting, character limits, and languages?
            Read our{' '}
            <Link href={BULK_UPLOAD_GUIDE_URL} external>
              guide
            </Link>
            .
          </Paragraph>
        </Flex>
        <Flex as="section" direction="column" gap="md" className={styles.step}>
          <Heading size="small">3. Upload your file</Heading>
          <UploadDropzone.Root
            onDrop={onUpload}
            onPaste={onUpload}
            className={styles.uploadDropzone}
          >
            <UploadDropzone.Overlay className={styles.dropzoneOverlay}>
              <Text size="lg" color="subtle" weight="bold">
                Drop a CSV file here
              </Text>
            </UploadDropzone.Overlay>
            <Flex
              direction="column"
              justify="center"
              align="center"
              gap="xs"
              fullHeight
              fullWidth
            >
              <Text size="md">Drag and drop your CSV file here or </Text>
              <FileInput
                onChange={handleFileChange}
                accept=".csv"
                id="csv-upload"
              >
                <Button variant="primary" size="medium">
                  <UploadIcon />
                  Upload File
                </Button>
              </FileInput>
              {error && (
                <Notice variant="error" className={styles.uploadNotice}>
                  {error}
                </Notice>
              )}
            </Flex>
          </UploadDropzone.Root>
        </Flex>
      </Flex>
    </BulkPostDialogBody>
  )
}
